import { AlertTriangle, BadgeCheck } from "lucide-react";
import { SettingsKpi } from "../../components/SettingsKpi";
import { LoadingLine } from "../../components/LoadingLine";
import {
  SYSTEM_SETTINGS_CATEGORIES,
  type SystemSettingsCategoryId,
} from "../systemSettings.constants";
import { useSystemSettings } from "../hooks/useSystemSettings";
import { SystemSettingsModuleSection } from "./SystemSettingsModuleSection";
import { CreditConversionTool } from "./CreditConversionTool";
import { NotificationAdminPanels } from "@/pages/notifications/components/NotificationAdminPanels";

interface SystemSettingsTabProps {
  categoryId: SystemSettingsCategoryId;
}

export function SystemSettingsTab({ categoryId }: SystemSettingsTabProps) {
  const category = SYSTEM_SETTINGS_CATEGORIES[categoryId];
  const settings = useSystemSettings(categoryId, category.modules);
  const CategoryIcon = category.icon;

  if (settings.loading) return <LoadingLine label={category.loadingLabel} />;

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-foreground">{category.title}</h3>
        <p className="mt-1 text-xs text-muted-foreground">{category.description}</p>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <SettingsKpi
          icon={CategoryIcon}
          label={category.kpiLabel}
          value={String(settings.settings.length)}
        />
        <SettingsKpi
          icon={AlertTriangle}
          label="Réglages sensibles"
          value={String(settings.criticalCount)}
          tone={settings.criticalCount > 0 ? "danger" : "neutral"}
        />
        <SettingsKpi
          icon={BadgeCheck}
          label="Modules concernés"
          value={String(settings.moduleCount)}
        />
      </div>

      {settings.dirtyCount > 0 && (
        <div className="border border-warning-border bg-warning-bg px-4 py-3 text-sm text-warning-text">
          {settings.dirtyCount} modification{settings.dirtyCount > 1 ? "s" : ""} en attente
          d'enregistrement.
        </div>
      )}

      {categoryId === "mail" && (
        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Configuration et diagnostic SMTP
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Testez la connexion SMTP et consultez les derniers envois depuis la zone dédiée aux
              e-mails.
            </p>
          </div>
          <NotificationAdminPanels />
        </section>
      )}

      {Object.entries(settings.grouped).map(([module, items]) => (
        <SystemSettingsModuleSection
          key={module}
          module={module}
          items={items}
          drafts={settings.drafts}
          saving={settings.saving}
          onDraftChange={settings.setDraft}
          onSave={settings.saveSetting}
        />
      ))}

      {categoryId === "financial" && <CreditConversionTool />}
    </div>
  );
}
